<template>
  <div>
    <Head title="Inventaris - Item Inv Type" />
    <h1 class="mb-8 text-3xl font-bold">
      Inventaris
      <span class="text-indigo-400 font-medium"> / </span>
      Item Inv Type
    </h1>

    <!-- Content column: 30% viewport width on large displays -->
    <div class="w-2/3 lg:w-1/2">
      <!-- Header: search + Add Main -->
      <div class="flex gap-4 items-center justify-between mb-6">
        <input
          v-model="search"
          type="text"
          placeholder="Cari kategori..."
          class="px-4 py-2 w-full border border-gray-300 focus:border-indigo-500 rounded shadow-sm focus:ring-1 focus:ring-indigo-500">
        <button
          type="button"
          class="btn-indigo"
          @click="openCreateModal(null)">
          + Tambah Kategori
        </button>
      </div>

      <!-- Empty state -->
      <div v-if="filteredMains.length === 0" class="py-12 text-center text-gray-500">
        Tidak ada data ditemukan.
      </div>

      <!-- Mains + nested Subs -->
      <div
        v-for="main in filteredMains"
        :key="main.id"
        class="mb-6 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <!-- Main card header -->
        <div class="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div>
            <div class="flex gap-2 items-baseline">
              <span class="text-indigo-600 font-mono font-semibold">{{ main.code }}.</span>
              <h3 class="text-gray-800 text-lg font-semibold">
                {{ main.name }}
              </h3>
            </div>
            <p v-if="main.desc" class="mt-0.5 text-gray-500 text-sm">
              {{ main.desc }}
            </p>
          </div>
          <div class="flex gap-2 items-center">
            <button
              type="button"
              class="btn-green btn-sm"
              @click="openEditModal(main, null)">
              Ubah
            </button>
            <button
              type="button"
              class="btn-indigo btn-sm"
              @click="openCreateModal(main)">
              + Sub
            </button>
          </div>
        </div>

        <!-- Subs list -->
        <div v-if="main.children && main.children.length">
          <div
            v-for="sub in main.children"
            :key="sub.id"
            class="flex items-center justify-between px-6 py-3 hover:bg-gray-50 border-t border-gray-100">
            <div class="flex gap-2 items-baseline min-w-0">
              <span class="text-gray-400 font-mono text-sm">{{ main.code }}.{{ sub.code }}</span>
              <span class="text-gray-700 truncate">{{ sub.name }}</span>
              <span v-if="sub.desc" class="ml-2 text-gray-400 text-sm truncate">— {{ sub.desc }}</span>
            </div>
            <div class="flex gap-2 items-center">
              <button
                type="button"
                class="btn-green btn-sm"
                @click="openEditModal(sub, main)">
                Ubah
              </button>
            </div>
          </div>
        </div>
        <div v-else class="px-6 py-3 text-gray-400 text-sm border-t border-gray-100">
          Belum ada sub kategori.
        </div>
      </div>
    </div>

    <!-- Modal: create/edit -->
    <Modal v-model="showModal" :title="isEditing ? 'Ubah Kategori' : editingParent ? 'Tambah Sub Kategori' : 'Tambah Kategori'">
      <form @submit.prevent="submitForm" class="space-y-4">
        <!-- Parent label (read-only, shown when adding sub) -->
        <div v-if="editingParent && !isEditing">
          <label class="block mb-1 text-gray-700 text-sm font-medium">Kategori Utama</label>
          <div class="px-3 py-2 text-gray-700 bg-gray-100 rounded">
            {{ editingParent.name }}
          </div>
        </div>

        <div>
          <label for="name" class="block mb-1 text-gray-700 text-sm font-medium">Nama</label>
          <input
            id="name"
            v-model="form.name"
            type="text"
            class="px-3 py-2 w-full border border-gray-300 focus:border-indigo-500 rounded shadow-sm focus:ring-1 focus:ring-indigo-500"
            required>
          <p v-if="errors.name" class="mt-1 text-red-600 text-sm">
            {{ errors.name }}
          </p>
        </div>

        <div>
          <label for="desc" class="block mb-1 text-gray-700 text-sm font-medium">Deskripsi</label>
          <input
            id="desc"
            v-model="form.desc"
            type="text"
            class="px-3 py-2 w-full border border-gray-300 focus:border-indigo-500 rounded shadow-sm focus:ring-1 focus:ring-indigo-500">
          <p v-if="errors.desc" class="mt-1 text-red-600 text-sm">
            {{ errors.desc }}
          </p>
        </div>

        <p v-if="errors.parent_id" class="text-red-600 text-sm">
          {{ errors.parent_id }}
        </p>
      </form>

      <template #footer>
        <button
          type="button"
          class="px-4 py-2 text-gray-700 text-sm hover:bg-gray-100 rounded"
          @click="showModal = false">
          Batal
        </button>
        <button
          type="button"
          class="btn-indigo"
          @click="submitForm"
          :disabled="submitting">
          {{ submitting ? 'Menyimpan...' : isEditing ? 'Simpan Perubahan' : 'Tambah' }}
        </button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout.vue'
import Modal from '@/Shared/Modal.vue'

defineOptions({ layout: Layout })

const props = defineProps({
    itemInvTypes: { type: Array, required: true },
})

// ── Search (client-side, no server round-trip) ─────────────────────
const search = ref('')
const filteredMains = computed(() => {
    const q = search.value.trim().toLowerCase()
    if (!q) return props.itemInvTypes
    return props.itemInvTypes
        .map((main) => ({
            ...main,
            children: main.children.filter((s) => s.name.toLowerCase().includes(q) || (s.desc && s.desc.toLowerCase().includes(q))),
        }))
        .filter((main) => main.name.toLowerCase().includes(q) || (main.desc && main.desc.toLowerCase().includes(q)) || main.children.length > 0)
})

// ── Modal state ─────────────────────────────────────────────────────
const showModal = ref(false)
const isEditing = ref(false)
const editingItem = ref(null)
const editingParent = ref(null)
const submitting = ref(false)
const errors = ref({})

const form = ref({ name: '', desc: '' })

function openCreateModal (parent) {
    isEditing.value = false
    editingItem.value = null
    editingParent.value = parent
    form.value = { name: '', desc: '' }
    errors.value = {}
    showModal.value = true
}

function openEditModal (item, parent) {
    isEditing.value = true
    editingItem.value = item
    editingParent.value = parent
    form.value = { name: item.name, desc: item.desc || '' }
    errors.value = {}
    showModal.value = true
}

function submitForm () {
    submitting.value = true
    errors.value = {}

    const payload = {
        name: form.value.name,
        desc: form.value.desc || '',
        parent_id: editingParent.value?.id ?? null,
    }

    const onFinish = () => {
        submitting.value = false
    }

    if (isEditing.value) {
        router.put(`/item_inv_types/${editingItem.value.id}`, payload, {
            preserveState: true,
            onFinish,
            onError: (e) => {
                errors.value = e
            },
            onSuccess: () => {
                showModal.value = false
            },
        })
    } else {
        if (!window.confirm('Kategori baru tidak dapat dihapus setelah dibuat. Lanjutkan?')) {
            submitting.value = false
            return
        }

        router.post('/item_inv_types', payload, {
            preserveState: true,
            onFinish,
            onError: (e) => {
                errors.value = e
            },
            onSuccess: () => {
                showModal.value = false
            },
        })
    }
}
</script>
